import { forwardRef, useState, useCallback } from "react";
import { Clock, CheckCircle2, AlertCircle } from "@semo/icons";
import { cn } from "@semo/shared-ui";
import { PaymentPendingDataDtoStatus } from "../types";
import { CountdownTimer } from "../atoms/CountdownTimer/CountdownTimer";
import type { ChatPaymentPendingCardProps } from "../types";
import { MessageCardWrapper } from "../layout/MessageCardWrapper";

/**
 * PaymentPendingCard - 입금 대기 중 알림 카드
 *
 * **입금 계좌는 `bankAccount` 가 실려 왔고, 아직 기다리는 중일 때만 그린다**(2026-08-28).
 * 두 종류의 카드가 같은 채팅에 섞여 있기 때문이다:
 *
 *   지금 것   — 안심결제 무통장. 세모가 대금을 받으므로 계좌를 봐야 입금할 수 있다.
 *   과거 것   — 안심결제를 닫아 두었던 기간의 기록. 계좌가 비어 있고, 그 시절 계좌로
 *               송금하면 안 된다. 지우지는 않는다 — 지우면 이력에 구멍이 난다.
 *
 * 만료·반려·취소된 건에도 계좌를 그리지 않는다. 그 건은 입금해도 아무 일이 일어나지 않는데
 * 계좌가 보이면 보내도 되는 것으로 읽힌다.
 *
 * [상세 보기]·[카드 결제] 는 되살리지 않았다 — 갈 곳이 없는 버튼이다.
 */
export const PaymentPendingCard = forwardRef<
  HTMLDivElement,
  ChatPaymentPendingCardProps
>(
  (
    {
      message,
      amount,
      sender,
      moid,
      bankAccount,
      expiresAt,
      onExpired,
      isExpired,
      status = PaymentPendingDataDtoStatus.PENDING,
      rejectionReason,
    },
    ref,
  ) => {
    const { createdAt } = message;

    // Local state to force re-render when timer expires
    const [isSelfExpired, setIsSelfExpired] = useState(false);

    // 상태 파생 변수
    const isApproved = status === PaymentPendingDataDtoStatus.APPROVED;
    const isRejected = status === PaymentPendingDataDtoStatus.REJECTED;
    const isCanceled = status === "CANCELED"; // Assuming CANCELED is not in the Enum yet or handled differently

    // 만료 상태 계산 (isExpired 명시적 값 or isSelfExpired or expiresAt 비교)
    // Parent passes boolean isExpired, but if timer triggers locally, we override to true
    // 단, 승인/반려/취소 상태에서는 만료 여부가 무의미하므로 false 처리 (UI 스타일링을 위해)
    const computedExpired =
      !isApproved &&
      !isRejected &&
      !isCanceled &&
      (isExpired === true ||
        isSelfExpired ||
        (expiresAt ? new Date(expiresAt) < new Date() : false));

    const handleExpired = useCallback(() => {
      setIsSelfExpired(true);
      onExpired?.();
    }, [onExpired]);

    // 눌렀다는 것을 화면으로 알려 준다. 계좌는 한 번에 옮겨 적기 어려운 값이라,
    // 복사가 됐는지 모르면 사용자가 결국 손으로 옮겨 적는다.
    const [isCopied, setIsCopied] = useState(false);
    const handleCopyAccount = useCallback(() => {
      if (!bankAccount) return;
      void navigator.clipboard
        ?.writeText(bankAccount)
        .then(() => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        })
        .catch(() => {
          // 복사가 막힌 브라우저(권한·비보안 컨텍스트)에서는 계좌가 그대로 보이므로
          // 손으로 옮겨 적을 수 있다. 실패를 따로 알리지 않는다.
        });
    }, [bankAccount]);

    const time = new Date(createdAt).toLocaleString("ko-KR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Format amount if it's a number
    const displayAmount =
      typeof amount === "number"
        ? `${amount.toLocaleString("ko-KR")}원`
        : amount;

    // 상태에 따른 스타일 및 텍스트 설정
    const getStatusConfig = () => {
      if (isApproved) {
        return {
          title: "입금 확인 완료",
          icon: <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6" />,
          borderColor:
            sender === "me" ? "border-green-500/30 " : "border-green-200 ",
          iconBg: "bg-green-100 ",
          iconColor: "text-green-600 ",
          titleColor: "text-slate-900 ",
          bgDecoration: "bg-green-50 ",
          detailsBg: "bg-green-50/50  border-green-100 ",
          footerText: "입금이 확인되어 계약이 체결되었습니다",
          footerIcon: (
            <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0 text-green-600 " />
          ),
        };
      }
      if (isRejected) {
        return {
          title: "입금 확인 불가",
          icon: <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6" />,
          borderColor:
            sender === "me" ? "border-red-500/30 " : "border-red-200 ",
          iconBg: "bg-red-100 ",
          iconColor: "text-red-600 ",
          titleColor: "text-slate-900 ",
          bgDecoration: "bg-red-50 ",
          detailsBg: "bg-red-50/50  border-red-100 ",
          footerText: "입금 내역을 확인할 수 없습니다",
          footerIcon: (
            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0 text-red-600 " />
          ),
        };
      }
      if (computedExpired || isCanceled) {
        return {
          title: "입금 대기 중", // 만료되어도 타이틀은 유지하되 배지로 표시
          icon: <Clock className="w-5 h-5 sm:w-6 sm:h-6" />,
          borderColor: "border-gray-300 ",
          iconBg: "bg-gray-100 ",
          iconColor: "text-gray-600 ",
          titleColor: "text-gray-600 ",
          bgDecoration: "bg-gray-100 ",
          detailsBg: "bg-gray-50/50  border-gray-200 ",
          footerText: "입금 시간이 만료되었습니다",
          footerIcon: (
            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-500" />
          ),
        };
      }
      // Default: PENDING
      return {
        title: "입금 대기 중",
        icon: <Clock className="w-5 h-5 sm:w-6 sm:h-6" />,
        borderColor:
          sender === "me" ? "border-amber-500/30 " : "border-amber-200 ",
        iconBg: "bg-amber-100 ",
        iconColor: "text-amber-600 ",
        titleColor: "text-slate-900 ",
        bgDecoration: "bg-amber-50 ",
        detailsBg: "bg-amber-50/50  border border-amber-100 ",
        footerText: "입금이 확인되면 계약이 성사됩니다.",
        footerIcon: <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />,
      };
    };

    const config = getStatusConfig();

    // 유효한 대기 상태인지 확인 (카드 결제 버튼 노출 조건)
    const isPendingValid =
      !computedExpired && !isApproved && !isRejected && !isCanceled;

    return (
      <MessageCardWrapper
        ref={ref}
        data-slot="chat-payment-pending-card"
        className={cn(
          "flex flex-col justify-start items-start overflow-hidden p-4 rounded-[14px] bg-white border relative shadow-sm transition-all",
          computedExpired || isCanceled
            ? "border-gray-200 opacity-70"
            : isApproved
              ? "border-green-500/30"
              : isRejected
                ? "border-red-500/30"
                : "border-[#fe9a00]/30",
        )}
        style={{
          boxShadow:
            "0px 1px 3px 0 rgba(0,0,0,0.1), 0px 1px 2px -1px rgba(0,0,0,0.1)",
        }}
      >
        {/* Header Section */}
        <div className="flex flex-col justify-start items-start self-stretch pb-3">
          <div className="flex justify-start items-center self-stretch gap-3">
            {/* Icon Container */}
            <div
              className={cn(
                "flex justify-center items-center w-12 h-12 relative overflow-hidden rounded-full shadow-sm shrink-0",
                computedExpired || isCanceled
                  ? "bg-gray-100"
                  : isApproved
                    ? "bg-green-100"
                    : isRejected
                      ? "bg-red-100"
                      : "bg-[#fef3c6]",
              )}
              style={{
                boxShadow:
                  "0px 1px 3px 0 rgba(254,154,0,0.1), 0px 1px 2px -1px rgba(254,154,0,0.1)",
              }}
            >
              {isApproved ? (
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              ) : isRejected ? (
                <AlertCircle className="w-6 h-6 text-red-600" />
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                >
                  <path
                    d="M12 6V12L16 14"
                    stroke={
                      computedExpired || isCanceled ? "#64748b" : "#E17100"
                    }
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                    stroke={
                      computedExpired || isCanceled ? "#64748b" : "#E17100"
                    }
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>

            {/* Title & Timing Info */}
            <div className="flex flex-col justify-start items-start flex-1 min-w-0">
              <div className="flex justify-start items-center self-stretch">
                <p className="flex-1 text-base font-semibold text-[#0f172b] truncate">
                  {config.title}
                </p>
              </div>
              <div className="flex justify-between items-center self-stretch relative mt-1">
                <p className="text-2xs text-[#898989] truncate">{time}</p>
                {isPendingValid && expiresAt && (
                  <div className="flex justify-center items-center px-2 py-0.5 rounded-[20px] bg-[#FEF3C6] border border-[#FEF3C6] ml-2 shrink-0">
                    <CountdownTimer
                      expiresAt={expiresAt}
                      onExpired={handleExpired}
                      showIcon={false}
                      className="text-2xs font-medium text-[#E17100]"
                    />
                  </div>
                )}
                {(computedExpired || isCanceled) && (
                  <div className="px-2 py-0.5 text-2xs font-semibold bg-gray-500 text-white rounded shrink-0 ml-2">
                    {isCanceled ? "취소됨" : "만료됨"}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Details Section */}
        <div className="flex flex-col justify-start items-start self-stretch pb-4">
          <div
            className={cn(
              "flex flex-col justify-start items-start self-stretch gap-3 p-3 rounded-[10px] border w-full",
              computedExpired || isCanceled
                ? "bg-gray-50 border-gray-200"
                : isApproved
                  ? "bg-green-50/50 border-green-100"
                  : isRejected
                    ? "bg-red-50/50 border-red-100"
                    : "bg-amber-50/50 border-[#fef3c6]",
            )}
          >
            {/* Amount Row */}
            <div className="flex justify-between items-center w-full px-2">
              <p className="text-xs text-[#62748e]">입금 금액</p>
              <p className="text-sm font-semibold text-[#0f172b]">
                {displayAmount}
              </p>
            </div>

            {/*
              계좌는 «아직 기다리는 중 + 계좌가 실려 온» 건에만. 만료·반려·과거 기록에
              보이면 입금해도 아무 일이 없는 계좌로 돈을 보내게 된다.
            */}
            {isPendingValid && bankAccount && (
              <div className="flex flex-col justify-start items-start w-full gap-1.5 px-2 pt-3 border-t border-black/5">
                <p className="text-xs text-[#62748e]">입금 계좌</p>
                <div className="flex items-center gap-2 w-full">
                  <p className="flex-1 text-sm font-semibold text-[#0f172b] break-all">
                    {bankAccount}
                  </p>
                  <button
                    type="button"
                    onClick={handleCopyAccount}
                    className="shrink-0 rounded-md border border-[#fef3c6] bg-white px-2 py-1 text-2xs font-medium text-[#E17100]"
                  >
                    {isCopied ? "복사됨" : "복사"}
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-col justify-start items-start w-full gap-1 px-2 pt-3 border-t border-black/5">
              <p className="text-xs text-[#62748e]">거래번호</p>
              <p className="text-2xs text-[#314158] break-all">{moid}</p>
            </div>
          </div>
        </div>

        {/* Rejection Reason (Only when REJECTED) */}
        {isRejected && rejectionReason && (
          <div className="self-stretch mb-4 p-2.5 bg-red-50 rounded-lg border border-red-100">
            <p className="text-2xs font-semibold text-red-600 mb-1">반려 사유</p>
            <p className="text-2xs text-red-800 leading-relaxed whitespace-pre-wrap">
              {rejectionReason}
            </p>
          </div>
        )}

        {/* Footer Notice Section */}
        <div className="flex flex-col justify-start items-start self-stretch pb-4 pl-1.5">
          <div className="flex justify-start items-center gap-1.5">
            <div className="flex justify-center items-center h-3.5 w-3 pt-0.5">
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-3 h-3"
              >
                <path
                  d="M6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11Z"
                  stroke={
                    computedExpired || isCanceled
                      ? "#94a3b8"
                      : isApproved
                        ? "#059669"
                        : "#45556C"
                  }
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.5 6L5.5 7L7.5 5"
                  stroke={
                    computedExpired || isCanceled
                      ? "#94a3b8"
                      : isApproved
                        ? "#059669"
                        : "#45556C"
                  }
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p
              className={cn(
                "text-xs",
                computedExpired || isCanceled
                  ? "text-gray-500"
                  : isApproved
                    ? "text-green-600"
                    : "text-[#45556c]",
              )}
            >
              {config.footerText}
            </p>
          </div>
        </div>
      </MessageCardWrapper>
    );
  },
);

PaymentPendingCard.displayName = "ChatPaymentPendingCard";
